import { useCallback, useState } from 'react'

const PDF_ERROR_MESSAGE = 'Could not generate the receipt PDF. Please try again.'
// Desktop-width capture viewport: narrower values trigger the receipt's own responsive
// rules and the PDF comes out cramped compared to the on-screen view.
const RECEIPT_RENDER_WIDTH_PX = 1100

type Html2PdfChain = {
  set: (options: Record<string, unknown>) => Html2PdfChain
  from: (element: HTMLElement) => Html2PdfChain
  save: () => Promise<void>
}

const loadHtml2Pdf = async (): Promise<() => Html2PdfChain> => {
  const mod = (await import('html2pdf.js')) as { default?: () => Html2PdfChain }
  // UMD bundle: default-export shape differs between dev and build interop.
  return mod.default ?? (mod as unknown as () => Html2PdfChain)
}

export const buildReceiptFileName = (receiptId: number | null): string =>
  receiptId !== null ? `receipt-${receiptId}.pdf` : 'receipt.pdf'

type ReceiptFrame = { frame: HTMLIFrameElement; body: HTMLElement }

const createReceiptFrame = (receiptHtml: string): ReceiptFrame | null => {
  const frame = document.createElement('iframe')
  frame.style.position = 'absolute'
  frame.style.left = '-10000px'
  frame.style.top = '0'
  frame.style.width = `${RECEIPT_RENDER_WIDTH_PX}px`
  frame.style.height = '1200px'
  // allow-same-origin keeps contentDocument reachable for capture; scripts stay blocked.
  frame.setAttribute('sandbox', 'allow-same-origin')
  document.body.appendChild(frame)
  const doc = frame.contentDocument
  if (!doc) {
    frame.remove()
    return null
  }
  doc.open()
  // .no-print emulates the receipt's own @media print rule (screen cascade only);
  // max-width matches the on-screen dialog so the page isn't mostly empty margin.
  doc.write(
    `<!doctype html><html><head><style>.no-print{display:none!important}.receipt-container{max-width:100%!important}</style></head><body>${receiptHtml}</body></html>`,
  )
  doc.close()
  return { frame, body: doc.body }
}

export const useReceiptPdf = () => {
  const [isPrinting, setIsPrinting] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)

  const resetPrintState = useCallback(() => {
    setIsPrinting(false)
    setPrintError(null)
  }, [])

  const printReceipt = useCallback(
    async (receiptHtml: string, receiptId: number | null): Promise<void> => {
      setIsPrinting(true)
      setPrintError(null)
      const target = createReceiptFrame(receiptHtml)
      if (!target) {
        setPrintError(PDF_ERROR_MESSAGE)
        setIsPrinting(false)
        return
      }
      try {
        const html2pdf = await loadHtml2Pdf()
        await html2pdf()
          .set({
            margin: 10,
            filename: buildReceiptFileName(receiptId),
            // Measured: PNG is lossless, so a page of text costs ~18x JPEG (22MB vs 1.4MB).
            image: { type: 'jpeg', quality: 0.95 },
            html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
            pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.receipt-section', '.info-row'] },
          })
          .from(target.body)
          .save()
      } catch {
        setPrintError(PDF_ERROR_MESSAGE)
      } finally {
        target.frame.remove()
        setIsPrinting(false)
      }
    },
    [],
  )

  return { printReceipt, resetPrintState, isPrinting, printError }
}
